// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, MenuItem, Nav, NavDropdown } from 'react-bootstrap';

// Internal dependencies
import { getMenu } from '@/modules/cms/client/api/cms.api';

/**
 * Header menu of CMS pages flagged for the site menu.
 *
 * Renders nothing when the menu is empty or the request fails — an empty
 * dropdown in everyone's header is worse than no dropdown, and a CMS outage
 * must not take the navigation with it.
 *
 * Two shapes, because the header has two very different layouts:
 * - `nav`    a NavDropdown, for the signed-in navbar alongside Support
 * - `inline` a plain Dropdown, for the signed-out welcome bar, which is a
 *            centred flex row rather than a nav
 * - `list-group` plain rows, for the mobile navigation page
 */
export default function CmsMenuNav({ variant }) {
  const { t } = useTranslation('cms');
  const [pages, setPages] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getMenu()
      .then(data => {
        if (!cancelled && Array.isArray(data)) {
          setPages(data);
        }
      })
      .catch(() => {
        // Deliberately silent: an absent menu is the correct degraded state.
        if (!cancelled) {
          setPages([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!pages.length) {
    return null;
  }

  const label = t('More');

  // Full-width rows for the mobile navigation page, which is a list-group
  // rather than a navbar — the dropdowns above are hidden-xs, so without
  // this the menu would be unreachable on small screens.
  if (variant === 'list-group') {
    return (
      <>
        {pages.map(page => (
          <a
            key={page.slug}
            className="list-group-item"
            href={`/pages/${page.slug}`}
          >
            {page.title}
          </a>
        ))}
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <Dropdown id="cms-menu-dropdown" className="cms-menu-nav-inline">
        <Dropdown.Toggle
          useAnchor
          className="btn btn-link header-more-text hidden-xs"
        >
          {label}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {pages.map(page => (
            <MenuItem key={page.slug} href={`/pages/${page.slug}`}>
              {page.title}
            </MenuItem>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  return (
    <Nav className="hidden-xs">
      <NavDropdown
        className="hidden-xs cursor-pointer"
        id="cms-menu-dropdown"
        title={label}
      >
        {pages.map(page => (
          <MenuItem key={page.slug} href={`/pages/${page.slug}`}>
            {page.title}
          </MenuItem>
        ))}
      </NavDropdown>
    </Nav>
  );
}

CmsMenuNav.propTypes = {
  variant: PropTypes.oneOf(['nav', 'inline', 'list-group']),
};

CmsMenuNav.defaultProps = {
  variant: 'nav',
};
